import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import './LifestyleOrderCardsModal.css';
import {
  Modal,
  Tabs,
  Row,
  Col,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Badge,
  Alert,
  Spin,
  Divider,
  Card,
  Tag,
  Tooltip,
  Form
} from 'antd';
import {
  SearchOutlined,
  ShoppingCartOutlined,
  FilterOutlined,
  ClearOutlined,
  SaveOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

import LifestyleOrderCard from './LifestyleOrderCard';
import useHealthSnapshotData from '../hooks/useHealthSnapshotData';
import MomentDatePicker from './MomentDatePicker.tsx';
import {
  fetchAllLifestyleOrderCards,
  getCardsByCategory,
  searchCards,
  getCardsByDifficulty,
  getCardsByTimeCommitment,
  DIFFICULTY_LEVELS,
  TIME_COMMITMENTS
} from '../utils/lifestyleOrderCards';
import {
  fetchLifestyleOrderSets,
  getOrderSetCards,
  getPersonalizedOrderSets,
  ORDER_SET_CATEGORIES
} from '../utils/lifestyleOrderSets';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;

const LifestyleOrderCardsModal = ({
  visible,
  onClose,
  patientId,
  onOrdersSelected
}) => {
  // State management
  const [activeTab, setActiveTab] = useState('individual-cards');
  const [selectedCards, setSelectedCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedTimeCommitment, setSelectedTimeCommitment] = useState('all');
  const [selectedOrderSet, setSelectedOrderSet] = useState(null);
  const [allLifestyleOrderCards, setAllLifestyleOrderCards] = useState([]);
  const [lifestyleOrderSets, setLifestyleOrderSets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Date selection state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [useCustomDates, setUseCustomDates] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const cards = await fetchAllLifestyleOrderCards();
      const sets = await fetchLifestyleOrderSets();
      setAllLifestyleOrderCards(cards);
      setLifestyleOrderSets(sets);
      setLoading(false);
    };
    loadData();
  }, []);

  // Get patient health data
  const {
    loading: healthDataLoading,
    error: healthDataError,
    tableData,
    systemDeviations,
    lifestyleDeviations,
    patientDemographics
  } = useHealthSnapshotData(patientId);

  // Prepare patient data for card relevance scoring with validation
  const patientData = useMemo(() => {
    if (healthDataError || !tableData) {
      return { tableData: [], systemDeviations: {}, lifestyleDeviations: {}, patientDemographics: null };
    }
    return { tableData, systemDeviations, lifestyleDeviations, patientDemographics };
  }, [tableData, systemDeviations, lifestyleDeviations, patientDemographics, healthDataError]);

  // Filter cards based on search and filters
  const filteredCards = useMemo(() => {
    let cards = allLifestyleOrderCards;

    // Apply search filter
    if (searchQuery.trim()) {
      cards = searchCards(searchQuery, cards);
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      cards = cards.filter(card => card.category === selectedCategory);
    }

    // Apply difficulty filter
    if (selectedDifficulty !== 'all') {
      cards = cards.filter(card => card.difficulty.level === selectedDifficulty);
    }

    // Apply time commitment filter
    if (selectedTimeCommitment !== 'all') {
      cards = cards.filter(card => card.timeCommitment.value === selectedTimeCommitment);
    }

    return cards;
  }, [searchQuery, selectedCategory, selectedDifficulty, selectedTimeCommitment]);

  // Get personalized recommendations with validation
  const personalizedRecommendations = useMemo(() => {
    if (!patientData.tableData || patientData.tableData.length === 0) {
      return []; // Return empty array instead of potentially crashing
    }
    return getPersonalizedOrderSets(patientData, lifestyleOrderSets);
  }, [patientData, lifestyleOrderSets]);

  // Handle card selection
  const handleCardSelect = useCallback((card) => {
    setSelectedCards(prev => {
      if (prev.find(c => c.id === card.id)) {
        return prev; // Already selected
      }
      return [...prev, card];
    });
  }, []);

  // Handle card deselection
  const handleCardDeselect = useCallback((card) => {
    setSelectedCards(prev => prev.filter(c => c.id !== card.id));
  }, []);

  // Handle order set selection
  const handleOrderSetSelect = (orderSet) => {
    const orderSetCards = getOrderSetCards(orderSet.id, lifestyleOrderSets, allLifestyleOrderCards);
    setSelectedCards(orderSetCards);
    setSelectedOrderSet(orderSet);
    setActiveTab('individual-cards'); // Switch to cards view to show selection
  };

  // Clear all selections
  const handleClearAll = () => {
    setSelectedCards([]);
    setSelectedOrderSet(null);
    setStartDate(null);
    setEndDate(null);
    setUseCustomDates(false);
  };

  // Helper function to parse duration and calculate end date
  const parseDurationToEndDate = (duration, startDate = new Date()) => {
    if (!duration) return null;

    const durationLower = duration.toLowerCase();
    let weeks = 0;
    let months = 0;

    // Parse common duration formats
    if (durationLower.includes('week')) {
      const weekMatch = durationLower.match(/(\d+)\s*week/);
      if (weekMatch) weeks = parseInt(weekMatch[1]);
    }

    if (durationLower.includes('month')) {
      const monthMatch = durationLower.match(/(\d+)\s*month/);
      if (monthMatch) months = parseInt(monthMatch[1]);
    }

    // If no specific duration found, default to 8 weeks
    if (weeks === 0 && months === 0) {
      weeks = 8;
    }

    const endDate = new Date(startDate);
    if (months > 0) {
      endDate.setMonth(endDate.getMonth() + months);
    } else {
      endDate.setDate(endDate.getDate() + (weeks * 7));
    }

    return endDate;
  };

  // Save selected orders
  const handleSaveOrders = () => {
    if (selectedCards.length === 0) {
      return;
    }

    // Use custom dates if provided, otherwise use current date
    const orderStartDate = useCustomDates && startDate ? startDate.toDate() : new Date();
    const orderEndDate = useCustomDates && endDate ? endDate.toDate() : null;

    // Convert cards to order format
    const orders = selectedCards.map(card => ({
      orderType: 'Lifestyle',
      startDate: orderStartDate,
      endDate: orderEndDate || parseDurationToEndDate(card.duration, orderStartDate),
      orderDetails: {
        cardId: card.id,
        title: card.title,
        category: card.category,
        subcategory: card.subcategory,
        description: card.description,
        smartGoals: card.smartGoals,
        duration: card.duration,
        targetParameters: card.targetParameters,
        clinicalRationale: card.clinicalRationale,
        monitoring: card.monitoring,
        expectedOutcomes: card.expectedOutcomes,
        contraindications: card.contraindications,
        evidenceLevel: card.evidenceLevel,
        citations: card.citations,
        difficulty: card.difficulty,
        timeCommitment: card.timeCommitment
      },
      status: 'Pending',
      priority: 'Routine'
    }));

    onOrdersSelected?.(orders);
    handleModalClose();
  };

  // Handle modal close with cleanup
  const handleModalClose = () => {
    setSelectedCards([]);
    setSelectedOrderSet(null);
    setStartDate(null);
    setEndDate(null);
    setUseCustomDates(false);
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setSelectedTimeCommitment('all');
    onClose();
  };

  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = [...new Set(allLifestyleOrderCards.map(card => card.category))];
    return cats.sort();
  }, [allLifestyleOrderCards]);

  // Render search and filters
  const renderFilters = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} md={8}>
        <Search
          placeholder="Search lifestyle interventions..."
          allowClear
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onSearch={setSearchQuery}
          prefix={<SearchOutlined />}
        />
      </Col>
      <Col xs={24} sm={12} md={5}>
        <Select
          style={{ width: '100%' }}
          placeholder="Category"
          value={selectedCategory}
          onChange={setSelectedCategory}
          allowClear
        >
          <Option value="all">All Categories</Option>
          {categories.map(cat => (
            <Option key={cat} value={cat}>{cat}</Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={5}>
        <Select
          style={{ width: '100%' }}
          placeholder="Difficulty"
          value={selectedDifficulty}
          onChange={setSelectedDifficulty}
          allowClear
        >
          <Option value="all">All Levels</Option>
          {Object.values(DIFFICULTY_LEVELS).map(level => (
            <Option key={level.level} value={level.level}>
              {level.icon} {level.level}
            </Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Select
          style={{ width: '100%' }}
          placeholder="Time Commitment"
          value={selectedTimeCommitment}
          onChange={setSelectedTimeCommitment}
          allowClear
        >
          <Option value="all">Any Duration</Option>
          {Object.values(TIME_COMMITMENTS).map(time => (
            <Option key={time.value} value={time.value}>
              {time.label}
            </Option>
          ))}
        </Select>
      </Col>
    </Row>
  );

  // Render shopping cart summary
  const renderShoppingCart = () => (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        backgroundColor: selectedCards.length > 0 ? '#f6ffed' : '#fafafa',
        border: selectedCards.length > 0 ? '1px solid #b7eb8f' : '1px solid #d9d9d9'
      }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: selectedCards.length > 0 ? 12 : 0 }}>
        <Col>
          <Space>
            <ShoppingCartOutlined style={{ fontSize: '16px' }} />
            <Text strong>
              Selected Interventions: {selectedCards.length}
            </Text>
            {selectedOrderSet && (
              <Tag color="blue">
                From: {selectedOrderSet.title}
              </Tag>
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            <Button
              size="small"
              icon={<ClearOutlined />}
              onClick={handleClearAll}
              disabled={selectedCards.length === 0}
            >
              Clear All
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={handleSaveOrders}
              disabled={selectedCards.length === 0}
            >
              Add to Orders ({selectedCards.length})
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Date Selection Section */}
      {selectedCards.length > 0 && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Space>
                <input
                  type="checkbox"
                  checked={useCustomDates}
                  onChange={(e) => setUseCustomDates(e.target.checked)}
                  style={{ marginRight: 4 }}
                />
                <Text style={{ fontSize: '12px' }}>Custom Dates</Text>
              </Space>
            </Col>
            <Col span={9}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text style={{ fontSize: '11px', color: '#666' }}>Start Date</Text>
                <MomentDatePicker
                  size="small"
                  placeholder="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  disabled={!useCustomDates}
                  style={{ width: '100%' }}
                />
              </Space>
            </Col>
            <Col span={9}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text style={{ fontSize: '11px', color: '#666' }}>End Date</Text>
                <MomentDatePicker
                  size="small"
                  placeholder="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  disabled={!useCustomDates}
                  style={{ width: '100%' }}
                />
              </Space>
            </Col>
          </Row>
          {!useCustomDates && (
            <Text style={{ fontSize: '11px', color: '#666', marginTop: 4, display: 'block' }}>
              Dates will be auto-calculated based on intervention duration
            </Text>
          )}
        </>
      )}
    </Card>
  );

  // Render individual cards tab
  const renderIndividualCardsTab = () => (
    <div>
      {renderFilters()}
      {renderShoppingCart()}
      
      <Row gutter={[16, 16]}>
        {filteredCards.map(card => (
          <Col xs={24} sm={12} md={8} lg={6} key={card.id}>
            <LifestyleOrderCard
              card={card}
              isSelected={selectedCards.some(c => c.id === card.id)}
              onSelect={handleCardSelect}
              onDeselect={handleCardDeselect}
              patientData={patientData}
            />
          </Col>
        ))}
      </Row>
      
      {filteredCards.length === 0 && (
        <Alert
          message="No lifestyle interventions found"
          description="Try adjusting your search criteria or filters."
          type="info"
          showIcon
        />
      )}
    </div>
  );

  // Render order sets tab
  const renderOrderSetsTab = () => (
    <div>
      {personalizedRecommendations.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            <InfoCircleOutlined /> Personalized Recommendations
          </Title>
          <Row gutter={[16, 16]}>
            {personalizedRecommendations.map(orderSet => (
              <Col xs={24} sm={12} md={8} key={orderSet.id}>
                <Card
                  size="small"
                  hoverable
                  style={{ border: '2px solid #1890ff20' }}
                  actions={[
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={() => handleOrderSetSelect(orderSet)}
                    >
                      Select This Set
                    </Button>
                  ]}
                >
                  <Badge.Ribbon text="Recommended" color="blue">
                    <Card.Meta
                      title={orderSet.title}
                      description={
                        <div>
                          <Text style={{ fontSize: '12px' }}>
                            {orderSet.description}
                          </Text>
                          <div style={{ marginTop: 8 }}>
                            <Tag color="green">{orderSet.cardIds.length} interventions</Tag>
                            <Tag color="blue">{orderSet.duration}</Tag>
                          </div>
                        </div>
                      }
                    />
                  </Badge.Ribbon>
                </Card>
              </Col>
            ))}
          </Row>
          <Divider />
        </div>
      )}

      <Title level={5}>All Clinical Order Sets</Title>
      <Row gutter={[16, 16]}>
        {lifestyleOrderSets.map(orderSet => (
          <Col xs={24} sm={12} md={8} key={orderSet.id}>
            <Card
              size="small"
              hoverable
              actions={[
                <Button 
                  size="small"
                  onClick={() => handleOrderSetSelect(orderSet)}
                >
                  Select This Set
                </Button>
              ]}
            >
              <Card.Meta
                title={orderSet.title}
                description={
                  <div>
                    <Text style={{ fontSize: '12px' }}>
                      {orderSet.description}
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Tag color="green">{orderSet.cardIds.length} interventions</Tag>
                      <Tag color="blue">{orderSet.duration}</Tag>
                      <Tag color="orange">{orderSet.category}</Tag>
                    </div>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  return (
    <Modal
      title="Lifestyle Order Cards - Clinical Prescription Deck"
      open={visible}
      onCancel={handleModalClose}
      width={1200}
      footer={null}
      destroyOnClose
      style={{ top: 20 }}
      className="lifestyle-order-cards-modal"
    >
      <Spin spinning={healthDataLoading || loading}>
        {healthDataError && (
          <Alert
            message="Error loading patient health data"
            description={healthDataError}
            type="warning"
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
        >
          <TabPane 
            tab={
              <span>
                <FilterOutlined />
                Individual Cards
                {selectedCards.length > 0 && (
                  <Badge count={selectedCards.length} style={{ marginLeft: 8 }} />
                )}
              </span>
            } 
            key="individual-cards"
          >
            {renderIndividualCardsTab()}
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <ShoppingCartOutlined />
                Order Sets
              </span>
            } 
            key="order-sets"
          >
            {renderOrderSetsTab()}
          </TabPane>
        </Tabs>
      </Spin>
    </Modal>
  );
};

LifestyleOrderCardsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientId: PropTypes.string.isRequired,
  onOrdersSelected: PropTypes.func.isRequired
};

export default LifestyleOrderCardsModal;
